/**
 * Maps Encamp's standard Program Areas to Statute Codes used in ECHO. Note TRI
 * will have statute code:EPCRA with lawSectionCode:313. Use the `statuteCodeToProgramArea(...)`
 * function to get the program area accurately. DON'T change order of the array; TRI must be before Chemicals.
 *
 * A second, narrower mapping of this same concept exists:
 * `ORDER_STATUTES_BY_PROGRAM` in
 * `apps/tasks/harmonize-violations/src/stages/harmonize/activeOrder.ts`. That
 * one decides whether an active EPA Order escalates a violation's severity to
 * HIGH, and admits only same-program statutes (RCRA→waste, CWA→water,
 * CAA→air) — so this map's adjacent statutes, CERCLA under Waste and
 * SDWA/MPRSA under Water, deliberately have no counterpart there. That is a
 * settled product decision as of 2026-08-11, not a gap awaiting alignment:
 * over-inclusion here only widens a browse/search result list, while there it
 * would mislabel a customer's compliance posture. Do not "fix" the divergence by
 * deriving one from the other. They will still drift for other reasons — if you
 * change either, look at the other.
 */
export const programAreaToStatuteCodes = new Map<string, string[]>([
  ["Air", ["CAA"]],
  ["TRI", ["EPCRA"]],
  ["Chemicals", ["FIFRA", "TSCA", "EPCRA"]],
  ["Waste", ["CERCLA", "MWTA", "RCRA", "MCRBMA", "MRBMA"]],
  ["Water", ["CWA", "MPRSA", "SDWA", "REFA", "OPA"]],
]);

const statuteCodeToProgramAreaMap = Array.from(programAreaToStatuteCodes.entries()).reduce(
  (acc, [programArea, statuteCodes]) =>
    statuteCodes.reduce((innerAcc, statuteCode) => innerAcc.set(statuteCode, programArea), acc),
  new Map<string, string>()
);

/**
 * Maps ECHO statueCodes/lawSectionCodes to encamp programAreas
 * @param statuteCode string
 * @param @optional lawSectionCode string
 * @returns string encamp program area
 */
export function statuteCodeToProgramArea(statuteCode: string, lawSectionCode?: string) {
  if (statuteCode === "EPCRA" && lawSectionCode === "313") {
    return "TRI";
  }

  const programArea = statuteCodeToProgramAreaMap.get(statuteCode) || "Unknown";
  return programArea;
}
